import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
    AlignmentType,
    Document,
    HeadingLevel,
    Packer,
    Paragraph,
    ShadingType,
    Table,
    TableCell,
    TableRow,
    TextRun,
    VerticalAlign,
    WidthType,
} from "docx";
import { getDefectColumnValue } from "./defectColumnValues.js";

const formatCurrency = (value) =>
    new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
    }).format(value);

function buildTableData(sortedDefects, columns) {
    const visibleColumns = columns.filter((col) => col.visible);
    const headers = visibleColumns.map((col) => col.label);
    const rows = sortedDefects.map((defect) =>
        visibleColumns.map((col) => {
            let value = getDefectColumnValue(defect, col);
            if (col.field === "Cost ($ AUD)") value = formatCurrency(value);
            if (value === null || value === undefined) value = "";
            return String(value);
        }),
    );
    return { visibleColumns, headers, rows };
}

export async function exportToExcel(sortedDefects, columns) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Defects");

    const visibleColumns = columns.filter((col) => col.visible);
    worksheet.columns = visibleColumns.map((col) => ({
        header: col.label,
        key: col.field,
        width: 25,
        style: { font: { name: "Arial", size: 10 } },
    }));

    sortedDefects.forEach((defect) => {
        const rowData = {};
        visibleColumns.forEach((col) => {
            rowData[col.field] = getDefectColumnValue(defect, col);
        });
        worksheet.addRow(rowData);
    });

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF000000" },
        };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const file = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(file, "Filtered_Defects.xlsx");
}

export function exportToPdf(sortedDefects, columns) {
    const { visibleColumns, headers, rows } = buildTableData(
        sortedDefects,
        columns,
    );

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text("Defects", 40, 40);

    const remedialIdx = visibleColumns.findIndex(
        (col) => col.field === "remedial action description",
    );
    const columnStyles = {};
    if (remedialIdx !== -1) columnStyles[remedialIdx] = { cellWidth: 142 };

    autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 60,
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: {
            fillColor: [0, 0, 0],
            textColor: [255, 255, 255],
            fontStyle: "bold",
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles,
    });

    doc.save("Filtered_Defects.pdf");
}

export async function exportToDocx(sortedDefects, columns) {
    const { headers, rows } = buildTableData(sortedDefects, columns);

    const headerCells = headers.map(
        (text) =>
            new TableCell({
                children: [
                    new Paragraph({
                        children: [
                            new TextRun({ text, bold: true, size: 22, color: "FFFFFF" }),
                        ],
                        alignment: AlignmentType.CENTER,
                    }),
                ],
                shading: { type: ShadingType.SOLID, color: "000000", fill: "000000" },
                verticalAlign: VerticalAlign.CENTER,
                margins: { top: 100, bottom: 100, left: 100, right: 100 },
            }),
    );

    const bodyRows = rows.map(
        (row, rowIndex) =>
            new TableRow({
                children: row.map(
                    (cellText) =>
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [new TextRun({ text: String(cellText), size: 20 })],
                                    alignment: AlignmentType.LEFT,
                                }),
                            ],
                            shading:
                                rowIndex % 2 === 0
                                    ? { type: ShadingType.SOLID, color: "FFFFFF", fill: "FFFFFF" }
                                    : { type: ShadingType.SOLID, color: "F5F5F5", fill: "F5F5F5" },
                            verticalAlign: VerticalAlign.CENTER,
                            margins: { top: 100, bottom: 100, left: 100, right: 100 },
                        }),
                ),
                height: { value: 300, rule: "atLeast" },
            }),
    );

    const table = new Table({
        rows: [
            new TableRow({
                children: headerCells,
                height: { value: 400, rule: "atLeast" },
            }),
            ...bodyRows,
        ],
        width: { size: 100, type: WidthType.PERCENTAGE },
        columnWidths: headers.map(() => Math.floor(10000 / headers.length)),
    });

    const doc = new Document({
        sections: [
            {
                properties: {
                    page: {
                        size: { width: 15840, height: 12240 },
                        margins: { top: 720, bottom: 720, left: 720, right: 720 },
                    },
                },
                children: [
                    new Paragraph({
                        children: [new TextRun({ text: "Defects", bold: true, size: 32 })],
                        heading: HeadingLevel.HEADING_1,
                        spacing: { after: 240 },
                    }),
                    table,
                ],
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, "Filtered_Defects.docx");
}
